import { useEffect, useState } from 'react';
import { earningsApi } from '@/lib/api';
import { useVendorStore } from '@/store/vendor';
import { logger } from '@/lib/logger';
import { formatCurrency } from '@/lib/format';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  DollarSign,
  TrendingUp,
  ShoppingBag,
  Calendar,
  Download,
} from 'lucide-react';

interface EarningsSummary {
  all_time: {
    revenue: number;
    orders: number;
  };
  today: {
    revenue: number;
    orders: number;
  };
  this_week: {
    revenue: number;
    orders: number;
  };
  this_month: {
    revenue: number;
    orders: number;
  };
}

interface EarningsDataPoint {
  date?: string;
  week?: string;
  month?: string;
  revenue: number;
  orders: number;
}

type Period = 'daily' | 'weekly' | 'monthly';

export default function EarningsPage() {
  const { stats } = useVendorStore();
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [chartData, setChartData] = useState<EarningsDataPoint[]>([]);
  const [period, setPeriod] = useState<Period>('daily');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSummary();
    loadChartData(period);
  }, []);

  useEffect(() => {
    loadChartData(period);
  }, [period]);

  const loadSummary = async () => {
    try {
      const data = await earningsApi.getSummary();
      setSummary(data);
    } catch (error) {
      logger.error('Failed to load earnings summary', error as Error);
    }
  };

  const loadChartData = async (selectedPeriod: Period) => {
    setIsLoading(true);
    try {
      let data: EarningsDataPoint[];

      switch (selectedPeriod) {
        case 'daily':
          data = await earningsApi.getDaily();
          break;
        case 'weekly':
          data = await earningsApi.getWeekly();
          break;
        case 'monthly':
          data = await earningsApi.getMonthly();
          break;
        default:
          data = [];
      }

      setChartData(data);
    } catch (error) {
      logger.error('Failed to load chart data', error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    if (chartData.length === 0) return;

    // Generate CSV
    const headers = ['Date', 'Revenue (₱)', 'Orders'];
    const rows = chartData.map((d) => [
      d.date || d.week || d.month,
      d.revenue.toFixed(2),
      d.orders.toString(),
    ]);

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `earnings_${period}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatXAxis = (value: string) => {
    if (period === 'daily') {
      const date = new Date(value);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    } else if (period === 'weekly') {
      return value.replace('W', 'W');
    } else {
      const [, month] = value.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return monthNames[parseInt(month) - 1];
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Earnings & Analytics</h1>
              <p className="text-muted-foreground">Track your revenue and order performance</p>
            </div>

            <button
              onClick={handleExport}
              disabled={chartData.length === 0}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* All Time */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm opacity-90">All Time</p>
                  <p className="text-2xl font-bold">{formatCurrency(summary.all_time.revenue)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm opacity-90">
                <ShoppingBag className="w-4 h-4" />
                <span>{summary.all_time.orders} orders</span>
              </div>
            </div>

            {/* Today */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Today</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(summary.today.revenue)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ShoppingBag className="w-4 h-4" />
                <span>{summary.today.orders} orders</span>
              </div>
            </div>

            {/* This Week */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">This Week</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(summary.this_week.revenue)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ShoppingBag className="w-4 h-4" />
                <span>{summary.this_week.orders} orders</span>
              </div>
            </div>

            {/* This Month */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">This Month</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(summary.this_month.revenue)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ShoppingBag className="w-4 h-4" />
                <span>{summary.this_month.orders} orders</span>
              </div>
            </div>
          </div>
        )}

        {/* Revenue Chart */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">Revenue Trend</h2>

            {/* Period Selector */}
            <div className="flex gap-2">
              <button
                onClick={() => setPeriod('daily')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  period === 'daily'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                Daily
              </button>
              <button
                onClick={() => setPeriod('weekly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  period === 'weekly'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => setPeriod('monthly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  period === 'monthly'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                Monthly
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="h-80 flex items-center justify-center">
              <p className="text-muted-foreground">Loading chart...</p>
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-80 flex items-center justify-center">
              <div className="text-center">
                <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No earnings data available</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Complete some orders to see your earnings trend
                </p>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey={period === 'daily' ? 'date' : period === 'weekly' ? 'week' : 'month'}
                  tickFormatter={formatXAxis}
                  stroke="#888"
                />
                <YAxis stroke="#888" tickFormatter={(value) => `₱${value}`} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [`₱${value.toFixed(2)}`, 'Revenue']}
                  labelFormatter={(label) => formatXAxis(label)}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Orders Chart */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Order Volume</h2>

          {isLoading ? (
            <div className="h-80 flex items-center justify-center">
              <p className="text-muted-foreground">Loading chart...</p>
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-80 flex items-center justify-center">
              <div className="text-center">
                <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No order data available</p>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey={period === 'daily' ? 'date' : period === 'weekly' ? 'week' : 'month'}
                  tickFormatter={formatXAxis}
                  stroke="#888"
                />
                <YAxis stroke="#888" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [value, 'Orders']}
                  labelFormatter={(label) => formatXAxis(label)}
                />
                <Bar dataKey="orders" fill="#10b981" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Performance Metrics */}
        {stats && (
          <div className="mt-8 bg-card border border-border rounded-xl p-6">
            <h2 className="text-xl font-bold text-foreground mb-6">Performance Metrics (Last 30 Days)</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Orders</p>
                <p className="text-3xl font-bold text-foreground">{stats.totalOrders}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Avg Prep Time</p>
                <p className="text-3xl font-bold text-foreground">
                  {stats.avgPrepTime}
                  <span className="text-lg text-muted-foreground ml-1">min</span>
                </p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Cancel Rate</p>
                <p className="text-3xl font-bold text-foreground">
                  {stats.cancelRate}
                  <span className="text-lg text-muted-foreground ml-1">%</span>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
